var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import { Box, styled } from "@mui/material";
import LevelFunctionMatrix from "./LevelFunctionMatrix";
import { useHeatmapLeadershipScores } from "../../hooks/heatmapScores";
import { useNavigate } from "../../hooks/navigate";
import { updateFilters } from "../../providers/FilterProvider";
import { getColorUndefinableDecider, Status } from "../../common/action";
import { getRoute } from "../../routes/getAllRoutes";
import styles from "./LevelFunctionMatrix.module.scss";
import { PersonOutline } from "@mui/icons-material";
import { AvailableFilters, getEntity } from "../../common/filters";
var withFocusedBorder = function (outlineColor) { return ({
    border: "1px solid ".concat(outlineColor),
    "&:focus": {
        border: "8px solid ".concat(outlineColor),
    },
}); };
var onEnterKey = function (toDo) { return function (event) {
    if ((event === null || event === void 0 ? void 0 : event.code) != "Enter") {
        return;
    }
    toDo();
}; };
var cellHeight = "58px";
var withLeaders = true;
export var ScoreTypes;
(function (ScoreTypes) {
    ScoreTypes["Capability"] = "capability";
    ScoreTypes["Potential"] = "potential";
    ScoreTypes["Functional"] = "functional";
    ScoreTypes["Organizational"] = "organizational";
    ScoreTypes["AI"] = "ai_score";
    ScoreTypes["Heidrick"] = "heidrick_score";
    ScoreTypes["Overall"] = "overall_score";
})(ScoreTypes || (ScoreTypes = {}));
var colorsForAction = {
    //changing colors for cells based on action
    act: {
        background: "var(--act-light)",
        border: "var(--act-dark)",
        color: "var(--primary-text)",
    },
    watch: {
        background: "var(--watch-light)",
        border: "var(--watch-dark)",
        color: "var(--primary-text)",
    },
    maintain: {
        border: "var(--maintain-dark)",
        background: "var(--maintain-light)",
        color: "var(--primary-text)",
    },
    nodata: {
        background: "var(--grey-color-400)",
        border: "#21362020",
    },
};
var newColorsForAction = {
    //changing colors for cells based on action
    act: {
        background: "var(--bad-1)",
        color: "var(--grey-color-90)",
        dividerColor: "var(--bad-2)",
    },
    watch: {
        background: "var(--neutral-1)",
        color: "var(--grey-color-90)",
        dividerColor: "var(--neutral-2)",
    },
    maintain: {
        background: "var(--good-1)",
        color: "var(--grey-color-90)",
        dividerColor: "var(--maintain-dark)",
    },
    nodata: {
        background: "var(--grey-color-200)",
    },
};
var cohortColors = {
    "1": {
        background: "var(--metric-lvl-1)",
        border: "var(--metric-lvl-2)",
        color: "var(--grey-color-90)",
    },
    "2": {
        background: "var(--metric-lvl-2)",
        border: "var(--metric-lvl-3)",
        color: "var(--grey-color-90)",
    },
    "3": {
        background: "var(--metric-lvl-3)",
        border: "var(--metric-lvl-4)",
        color: "var(--grey-color-90)",
    },
    "4": {
        background: "var(--metric-lvl-4)",
        border: "var(--metric-lvl-5)",
        color: "var(--background-color)",
    },
    "5": {
        background: "var(--metric-lvl-4)",
        border: "var(--metric-lvl-5)",
        color: "var(--background-color)",
    },
    nodata: {
        background: "var(--grey-color-400)",
        border: "#00213620",
    },
};
/** Normalize heatmap row/column segment for drill-down URL (ALL_row, ALL_col, ALL -> "All"). */
var cohortSegmentToUrl = function (value) {
    if (value == null || value === "") {
        return "All";
    }
    var v = String(value);
    if (v === "ALL" ||
        v === "ALL_row" ||
        v === "ALL_col" ||
        v.toLowerCase() === "all") {
        return "All";
    }
    return v;
};
var onScoreTypeClick = function (scoreType, secondaryPageUrl) {
    return function (roles, level, navigate) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        var filters = {
            scoreType: scoreType,
        };
        roles.forEach(function (item) {
            if (item.startsWith("F")) {
                filters[getEntity(AvailableFilters.BusinessFunctions)] = [item];
            }
            if (item.startsWith("SF")) {
                filters[getEntity(AvailableFilters.BusinessSubFunctions)] = [item];
            }
        });
        // if no level is selected, don't add it.
        if (!level.toLowerCase().startsWith("all")) {
            filters[getEntity(AvailableFilters.JobLevels)] = [level];
        }
        updateFilters(filters);
        navigate(secondaryPageUrl || getRoute("AssessmentAll"));
    };
};
export var getLeadershipDataCell = function (scoreFunctor, formatScore, onClick, getScoreColor, colorsForScore) {
    return function (_a) {
        var getNameByLookupCode = _a.getNameByLookupCode, level = _a.level, roles = _a.roles, isActive = _a.isActive, score = _a.score, expandCell = _a.expandCell;
        //check score
        var Component = function () {
            var _a;
            var _b;
            var navigate = useNavigate();
            var heading = roles[1]
                ? "".concat(getNameByLookupCode(roles[1]), " with Sub-Function ").concat(getNameByLookupCode(roles[0]))
                : getNameByLookupCode(roles[0]);
            var ariaLabel = "".concat(formatScore(scoreFunctor(score)), " ").concat(getNameByLookupCode(level), " doing ").concat(heading);
            var action = getScoreColor(score);
            var colorsToUse = colorsForScore || colorsForAction;
            var _c = colorsToUse.nodata, borderColor = _c.border, backgroundColor = _c.background, color = _c.color;
            var dividerColor = (_b = colorsToUse[action]) === null || _b === void 0 ? void 0 : _b.dividerColor;
            try {
                (_a = colorsToUse[action], borderColor = _a.border, backgroundColor = _a.background, color = _a.color);
            }
            catch (e) {
                console.error("No color found for action: ".concat(action, " in colorsForAction. Using no data colors"));
            }
            var borderStyle = isActive()
                ? { border: "none" }
                : withFocusedBorder(borderColor);
            var StyledCell = styled(Box)(__assign(__assign(__assign({ background: backgroundColor, color: color, borderRadius: "14px" }, borderStyle), (action == "nodata"
                ? { pointerEvents: "none", cursor: "default" }
                : {})), { "&:hover": __assign({}, (action == "nodata"
                    ? {}
                    : {
                        outline: "2px solid ".concat(borderColor),
                        cursor: "pointer",
                    })), "&:focus": {
                    outline: "none",
                    border: "3px solid ".concat(borderColor),
                    position: "relative",
                    bottom: "2px",
                    right: "2px",
                }, width: "100%", height: cellHeight, font: "800 16px/24px var(--font-stack-full-medium)", display: "flex", justifyContent: "center", alignItems: "center" }));
            var LeaderStyledCell = styled(Box)(__assign(__assign(__assign({ background: backgroundColor, color: color, borderRadius: "14px" }, borderStyle), (action == "nodata"
                ? { pointerEvents: "none", cursor: "default" }
                : {})), { "&:hover": __assign({}, (action == "nodata"
                    ? {}
                    : {
                        outline: "2px solid ".concat(borderColor),
                        cursor: "pointer",
                    })), "&:focus": {
                    outline: "none",
                    border: "3px solid ".concat(borderColor),
                    position: "relative",
                    bottom: "2px",
                    right: "2px",
                }, width: "100%", height: cellHeight, display: "flex", justifyContent: "center", alignItems: "center" }));
            var StylePercent = styled(Box)({
                font: "500 20px var(--font-stack-full-medium)",
                marginRight: "16px",
                paddingTop: "4px",
            });
            var StyleLeader = styled(Box)({
                font: "300 16px var(--font-stack-full-regular)",
                display: "flex",
                marginLeft: "16px",
                alignItems: "center",
            });
            var StyleDivider = styled(Box)({
                height: "18px",
                width: "1px",
                backgroundColor: dividerColor,
            });
            var text = formatScore(scoreFunctor(score));
            if (Array.isArray(text)) {
                return (_jsxs(LeaderStyledCell, __assign({}, (ariaLabel ? { "aria-label": ariaLabel } : {}), { tabIndex: action == "nodata" ? -1 : 0, onClick: action == "nodata"
                        ? function () { return null; }
                        : function () { return onClick && onClick(roles, level, navigate); }, onKeyPress: action == "nodata" ? undefined : onEnterKey(expandCell) }, { children: [_jsx(StylePercent, { children: text[0] }), _jsx(StyleDivider, {}), _jsxs(StyleLeader, { children: [_jsx(PersonOutline, { style: { height: "16px" } }), _jsx("div", { children: text[1] })] })] })));
            }
            return (_jsx(StyledCell, __assign({}, (ariaLabel ? { "aria-label": ariaLabel } : {}), { tabIndex: action == "nodata" ? -1 : 0, onClick: action == "nodata"
                    ? function () { return null; }
                    : function () { return onClick && onClick(roles, level, navigate); }, onKeyPress: action == "nodata" ? undefined : onEnterKey(expandCell) }, { children: text })));
        };
        return _jsx(Component, {});
    };
};
var scoreFunctor = function (field) {
    return function (score) {
        if ((score === null || score === void 0 ? void 0 : score[field]) == null) {
            return;
        }
        return score === null || score === void 0 ? void 0 : score[field];
    };
};
var scoreFunctorPlusLeaders = function (field) {
    return function (score) {
        if ((score === null || score === void 0 ? void 0 : score[field]) == null) {
            return;
        }
        return __assign(__assign({}, score === null || score === void 0 ? void 0 : score[field]), { leaders: (score === null || score === void 0 ? void 0 : score.leaders) || 0 });
    };
};
var atRiskScoreFormatter = function (field) {
    return function (score) {
        var cell = score === null || score === void 0 ? void 0 : score[field];
        if ((cell === null || cell === void 0 ? void 0 : cell.value) == null) {
            return;
        }
        return "".concat(Number(cell === null || cell === void 0 ? void 0 : cell.value).toFixed(0), "% (").concat(Number(cell === null || cell === void 0 ? void 0 : cell.count).toFixed(0), ")");
    };
};
var atRiskStateFunctor = function (field) { return function (score) { var _a; return ((_a = score === null || score === void 0 ? void 0 : score[field]) === null || _a === void 0 ? void 0 : _a.state) || Status.NoData; }; };
var stateFunctor = function (field) {
    return function (score) {
        var _a, _b;
        if (((_a = score === null || score === void 0 ? void 0 : score[field]) === null || _a === void 0 ? void 0 : _a.state) == null) {
            return "nodata";
        }
        return (_b = score === null || score === void 0 ? void 0 : score[field]) === null || _b === void 0 ? void 0 : _b.state;
    };
};
export var starsScoreFormatter = function (cell) {
    if (cell == null || cell.value == null || isNaN(cell.value)) {
        return;
    }
    return Number(cell === null || cell === void 0 ? void 0 : cell.value).toFixed(2);
};
export var percentageScoreFormatter = function (cell) {
    if (cell == null || cell.value == null || isNaN(cell.value)) {
        return;
    }
    return "".concat(Number(cell === null || cell === void 0 ? void 0 : cell.value).toFixed(0), "%");
};
export var percentageScoreFormatterWithLeader = function (cell) {
    var _a, _b;
    if (cell == null || cell.value == null || isNaN(cell.value)) {
        return;
    }
    if (Number(cell === null || cell === void 0 ? void 0 : cell.value) < 5) {
        return ["".concat(((Number(cell === null || cell === void 0 ? void 0 : cell.value) / 5) * 100).toFixed(0), "%"), cell === null || cell === void 0 ? void 0 : cell.leaders];
    }
    return ["".concat((_b = (_a = cell === null || cell === void 0 ? void 0 : cell.value) === null || _a === void 0 ? void 0 : _a.toFixed(0)) !== null && _b !== void 0 ? _b : "", "%"), cell === null || cell === void 0 ? void 0 : cell.leaders];
};
export var percentageOfScoreFormatterBase = function (cell, totalCount) {
    if (cell == null) {
        return;
    }
    if (totalCount == null) {
        totalCount =
            Math.round((100 * Number(cell.count)) / Number(cell.value)) || 0;
    }
    return (_jsxs(Box, __assign({ component: "span" }, { children: [_jsx(Box, __assign({ component: "span" }, { children: "".concat(Number(cell.value).toFixed(0), "%") })), _jsx(Box, __assign({ component: "span", sx: { fontSize: "13px" } }, { children: " (".concat(Number(cell.count).toFixed(0), " of ").concat(totalCount, ")") }))] })));
};
export var percentageAndCountOfScoreFormatter = function (score, total) {
    if (score == null) {
        return;
    }
    return "".concat(Number(100 * (score / total)).toFixed(0), "% (").concat(Number(score).toFixed(0));
};
var hasIntelligenceData = function (score) {
    return (score === null || score === void 0 ? void 0 : score.state) !== "nodata";
};
var RiskTitleLevelFunctionMatrix = function (props) {
    return (_jsx(LevelFunctionMatrix, __assign({}, props, { highRiskTitle: {
            teams: "Teams with significant gaps",
            individual: "Leaders with significant gaps",
        } })));
};
export var CapabilityLevelFunctionMatrix = function () { return (_jsx(RiskTitleLevelFunctionMatrix, { useHeatmapScores: useHeatmapLeadershipScores("Capabilities"), getExpandedCell: function () { return null; }, hasData: hasIntelligenceData, getDataCell: getLeadershipDataCell(scoreFunctor("capability"), percentageScoreFormatter, onScoreTypeClick(ScoreTypes.Capability), stateFunctor("capability"), colorsForAction) })); };
export var PotentialLevelFunctionMatrix = function () { return (_jsx(RiskTitleLevelFunctionMatrix, { useHeatmapScores: useHeatmapLeadershipScores("Potential"), getExpandedCell: function () { return null; }, hasData: hasIntelligenceData, getDataCell: getLeadershipDataCell(scoreFunctor("potential"), percentageScoreFormatter, onScoreTypeClick(ScoreTypes.Potential), stateFunctor("potential"), colorsForAction) })); };
export var OrganizationalLevelFunctionMatrix = function () { return (_jsx(RiskTitleLevelFunctionMatrix, { useHeatmapScores: useHeatmapLeadershipScores("Organizational"), getExpandedCell: function () { return null; }, hasData: hasIntelligenceData, getDataCell: getLeadershipDataCell(scoreFunctor("organizational"), percentageScoreFormatter, onScoreTypeClick(ScoreTypes.Organizational), stateFunctor("organizational"), colorsForAction) })); };
export var FunctionalLevelFunctionMatrix = function () { return (_jsx(RiskTitleLevelFunctionMatrix, { useHeatmapScores: useHeatmapLeadershipScores("Functional"), getExpandedCell: function () { return null; }, hasData: hasIntelligenceData, getDataCell: getLeadershipDataCell(scoreFunctor("functional"), percentageScoreFormatter, onScoreTypeClick(ScoreTypes.Functional), stateFunctor("functional"), colorsForAction) })); };
export var ImpactLevelFunctionMatrix = function (_a) {
    var riskRole = _a.riskRole, extraProps = __rest(_a, ["riskRole"]);
    return (_jsx(LevelFunctionMatrix, __assign({ useHeatmapScores: useHeatmapLeadershipScores(riskRole ? riskRole : "Functional") }, extraProps, { hasData: hasIntelligenceData, getExpandedCell: function () { return null; }, getDataCell: getLeadershipDataCell(scoreFunctorPlusLeaders(riskRole ? riskRole : "functional"), //get scores
        percentageScoreFormatterWithLeader, // formating to percentage
        onScoreTypeClick(riskRole ? ScoreTypes[riskRole] : ScoreTypes.Functional), stateFunctor(riskRole ? riskRole : "functional"), newColorsForAction, // change colors here
        withLeaders), isNew: true })));
};
export var AiScoreLevelFunctionMatrix = function () { return (_jsx(RiskTitleLevelFunctionMatrix, { useHeatmapScores: useHeatmapLeadershipScores("AI"), getExpandedCell: function () { return null; }, hasData: hasIntelligenceData, getDataCell: getLeadershipDataCell(scoreFunctor("ai_score"), starsScoreFormatter, onScoreTypeClick(ScoreTypes.AI), stateFunctor("ai_score"), colorsForAction) })); };
export var HeidrickScoreLevelFunctionMatrix = function () { return (_jsx(RiskTitleLevelFunctionMatrix, { useHeatmapScores: useHeatmapLeadershipScores("Heidrick"), getExpandedCell: function () { return null; }, hasData: hasIntelligenceData, getDataCell: getLeadershipDataCell(scoreFunctor("heidrick_score"), starsScoreFormatter, onScoreTypeClick(ScoreTypes.Heidrick), stateFunctor("heidrick_score"), colorsForAction) })); };
export var OverallScoreLevelFunctionMatrix = function () { return (_jsx(RiskTitleLevelFunctionMatrix, { useHeatmapScores: useHeatmapLeadershipScores("Match"), getExpandedCell: function () { return null; }, hasData: hasIntelligenceData, getDataCell: getLeadershipDataCell(scoreFunctor("overall_score"), starsScoreFormatter, onScoreTypeClick(ScoreTypes.Overall), stateFunctor("overall_score"), colorsForAction) })); };
var getCohortScoreColor = function (mapScore) {
    return function (score) {
        if (!score ||
            score.state === "nodata" ||
            (score.score == null && score.count == null) ||
            !score.score ||
            !score.count) {
            return "nodata";
        }
        return mapScore ? mapScore({ score: score.score }) : "nodata";
    };
};
export var getCohortHeatmapLegendScore = function (score, showScorePercentage) { return Number(score.toFixed(showScorePercentage ? 0 : 1)); };
export var getCohortCell = function (colors, mapScore, heatmapLegendImages, showScorePercentage) {
    if (showScorePercentage === void 0) { showScorePercentage = true; }
    return function (routeKey) {
        return getLeadershipDataCell(function (_a) {
            var score = _a.score, count = _a.count;
            if (!score || !count) {
                return _jsx(_Fragment, {});
            }
            var scoreValues = showScorePercentage
                ? score === null || score === void 0 ? void 0 : score.toFixed(0)
                : score === null || score === void 0 ? void 0 : score.toFixed(1);
            var legendScore = getCohortHeatmapLegendScore(score, showScorePercentage);
            return (_jsxs("div", __assign({ className: styles.cohortCell }, { children: [_jsxs("div", __assign({ className: styles.cohortCellScore }, { children: [heatmapLegendImages && _jsx(_Fragment, { children: heatmapLegendImages(legendScore) }), _jsx("div", { children: "".concat(scoreValues).concat(showScorePercentage ? "%" : "") })] })), _jsx("div", { className: styles.cohortCellPipe }), _jsxs("div", __assign({ className: styles.cohortCellIcon }, { children: [_jsx("div", __assign({ className: styles.cohortPerson }, { children: _jsx(PersonOutline, { style: { height: "16px" } }) })), _jsx("div", { children: count })] }))] })));
        }, function (a) { return a; }, function (project, role, navigate) {
            var rowVal = cohortSegmentToUrl(Array.isArray(project) ? project === null || project === void 0 ? void 0 : project[0] : project);
            var colVal = cohortSegmentToUrl(role);
            navigate(routeKey
                .replace(":role", encodeURIComponent(colVal))
                .replace(":project", encodeURIComponent(rowVal)));
        }, getCohortScoreColor(mapScore), colors);
    };
};
export function LowPerformerLevelFunctionMatrix(_a) {
    var scoreType = _a.scoreType, useScores = _a.useScores, scoreNormalizer = _a.scoreNormalizer, highRiskTitle = _a.highRiskTitle, highRiskTitleFormatter = _a.highRiskTitleFormatter, onClickIndividualRisk = _a.onClickIndividualRisk, secondaryPageUrl = _a.secondaryPageUrl;
    var normalizedScore = (scoreNormalizer || (function (value) { return value; }))(scoreType);
    return (_jsx(LevelFunctionMatrix, { useHeatmapScores: useScores(normalizedScore), getExpandedCell: function () { return null; }, getDataCell: getLeadershipDataCell(function (value) { return value; }, atRiskScoreFormatter(scoreType), onScoreTypeClick(scoreType, secondaryPageUrl), atRiskStateFunctor(scoreType), colorsForAction), highRiskTitle: highRiskTitle, highRiskTitleFormatter: highRiskTitleFormatter, secondaryPageUrl: secondaryPageUrl, onClickIndividualRisk: onClickIndividualRisk }));
}
var getColorKey = function (colors) {
    return "".concat(colors.color, "-").concat(colors.border);
};
export var mapCohortColorsForHeatmap = function (colors, fontColor) {
    return colors.reduce(function (current, color) {
        var _a;
        return (__assign(__assign({}, current), (_a = {}, _a[getColorKey(color)] = {
            background: color.color || "black",
            border: color.border || "black",
            color: fontColor[color.color] || "black",
        }, _a)));
    }, { nodata: cohortColors.nodata });
};
export var getCohortColorForHeatmap = function (colors) {
    var decider = getColorUndefinableDecider(colors);
    return function (_a) {
        var score = _a.score;
        var colorDescription = decider(score);
        return colorDescription && getColorKey(colorDescription);
    };
};
